import HyperDX from "@hyperdx/browser";
import { Attributes } from "@opentelemetry/api";

type HyperDxAction =
  | "loginWithEmail"
  | "loginWithGoogle"
  | "logout"
  | "resendVerificationEmail"
  | "verifyActionCode"
  | "verifyPasswordResetCode"
  | "sendPasswordResetEmail"
  | "confirmPasswordReset"
  | "signInWithEmailLink"
  | "checkExpired"
  | "refreshUserInfo";

type AttributeValue =
  | string
  | number
  | boolean
  | Array<string | number | boolean>;

interface LogOptions {
  userId?: string;
  userRole?: "SuperUser" | "Stakeholder" | "Freelancer";
  additionalContext?: Record<string, AttributeValue>;
}

const isProduction = process.env.NODE_ENV === "production";

export const useHyperDxAction = () => {
  const logAction = (
    action: HyperDxAction,
    details: Record<string, AttributeValue>,
    options?: LogOptions
  ) => {
    if (!isProduction) {
      console.debug("[HyperDX ActionDebug]", { action, details, options });
      return;
    }

    try {
      const sanitizedDetails = Object.fromEntries(
        Object.entries(details).map(([k, v]) => [k, String(v)])
      );
      HyperDX.addAction(action, sanitizedDetails);
    } catch (error) {
      console.error("Logging failed:", error);
    }
  };

  const sanitizeContext = (
    context: Record<string, AttributeValue>
  ): Attributes =>
    Object.fromEntries(Object.entries(context).map(([k, v]) => [k, String(v)]));

  const logError = (
    error: any,
    context: Record<string, AttributeValue>,
    options?: LogOptions
  ) => {
    if (!isProduction) {
      console.debug("[HyperDX Action Debug] Error:", {
        error,
        context,
        options,
      });
      return;
    }

    try {
      HyperDX.recordException(error, sanitizeContext(context));
    } catch (err) {
      console.error("Error logging failed:", err);
    }
  };

  return {
    logAction,
    logError,
    isEnabled: isProduction,
  };
};
